import { useState, useEffect, useRef, useCallback } from "react";
import { jwtFetch } from "./utils/apiClient";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { config } from "./Components/Config";
import Menu from "./Components/Menu";
import Header from "./Components/Header";

const NotifEmail = () => {
    const [data, setData] = useState([]);
    const [tableSearch, setTableSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [isLoadingPage, setIsLoadingPage] = useState(true);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 1
    });
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);
    const [modalType, setModalType] = useState('create');
    const [currentData, setCurrentData] = useState({});
    const [userOptions, setUserOptions] = useState([]);
    const searchInputRef = useRef(null);
    const crmUser = JSON.parse(localStorage.getItem('crmUser'));

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(tableSearch);
        }, 500);
        return () => clearTimeout(timer);
    }, [tableSearch]);

    const getData = useCallback(async () => {
        setIsLoadingPage(true);
        const searchQuery = debouncedSearch ? `&search=${encodeURIComponent(debouncedSearch)}` : '';
        const url = `${config.SUPPORT_ENDPOINT}receive-notif-emails?page=${pagination.page}&limit=${pagination.limit}${searchQuery}`;

        try {
            const response = await jwtFetch(url);
            const result = await response.json();
            if (result.success) {
                setData(result.data);
                setPagination(prev => ({
                    ...prev,
                    total: result.meta.total,
                    totalPages: result.meta.totalPages,
                    page: result.meta.page,
                    limit: result.meta.limit
                }));
            } else {
                alert('Failed to fetch data');
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            alert('Error fetching data');
        }
        setIsLoadingPage(false);
    }, [pagination.page, pagination.limit, debouncedSearch]);

    useEffect(() => {
        getData();
    }, [getData]);

    const fetchUsers = async () => {
        try {
            const response = await jwtFetch(`${config.ENDPOINT}get/user`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({})
            });
            const json = await response.json();
            if (json.status === 200) {
                setUserOptions(json.data.map(u => ({
                    value: u.email_address,
                    label: `${u.full_name} (${u.email_address})`,
                    full_name: u.full_name,
                    email_address: u.email_address
                })));
            }
        } catch (error) {
            console.error("Error fetching users:", error);
        }
    };

    const clearSearch = () => {
        setTableSearch('');
        if (searchInputRef.current) {
            searchInputRef.current.focus();
        }
    };

    const handleCreate = async () => {
        setModalType('create');
        setCurrentData({});
        await fetchUsers();
        setShowModal(true);
    };

    const handleEdit = async (item) => {
        setModalType('update');
        setCurrentData(item);
        await fetchUsers();
        setShowModal(true);
    };

    const handleDeleteClick = (id) => {
        setDeleteId(id);
        setShowDeleteModal(true);
    };

    const confirmDelete = async () => {
        if (!deleteId) return;
        setIsLoadingPage(true);
        try {
            const response = await fetch(`${config.SUPPORT_ENDPOINT}receive-notif-emails/${deleteId}`, {
                method: 'DELETE'
            });
            if (response.ok) {
                getData();
                setShowDeleteModal(false);
                setDeleteId(null);
            } else {
                alert('Failed to delete item');
            }
        } catch (error) {
            console.error(error);
            alert('Error deleting item');
        }
        setIsLoadingPage(false);
    };

    const handleSave = async () => {
        setIsLoadingPage(true);
        try {
            const method = modalType === 'create' ? 'POST' : 'PUT';
            const url = modalType === 'create'
                ? `${config.SUPPORT_ENDPOINT}receive-notif-emails`
                : `${config.SUPPORT_ENDPOINT}receive-notif-emails/${currentData.id}`;

            const body = { ...currentData };

            const response = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(body)
            });

            const result = await response.json();
            if (response.ok) {
                getData();
                setShowModal(false);
            } else {
                alert(result.message || 'Failed to save');
            }
        } catch (error) {
            console.error(error);
            alert('Error saving data');
        }
        setIsLoadingPage(false);
    };

    const renderPaginationItems = () => {
        const { page, totalPages } = pagination;
        const items = [];

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                items.push(i);
            }
        } else {
            if (page <= 4) {
                for (let i = 1; i <= 5; i++) items.push(i);
                items.push('...');
                items.push(totalPages);
            } else if (page >= totalPages - 3) {
                items.push(1);
                items.push('...');
                for (let i = totalPages - 4; i <= totalPages; i++) items.push(i);
            } else {
                items.push(1);
                items.push('...');
                items.push(page - 1);
                items.push(page);
                items.push(page + 1);
                items.push('...');
                items.push(totalPages);
            }
        }

        return items.map((item, index) => (
            <li key={index} className="page-item">
                {item === '...' ? (
                    <span className="page-link border-0 text-secondary bg-transparent" style={{ width: '32px', height: '32px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>...</span>
                ) : (
                    <button
                        className={`page-link border-0 rounded-circle d-flex align-items-center justify-content-center ${page === item ? 'bg-gold text-white shadow-sm' : 'text-secondary bg-transparent'}`}
                        style={{ width: '32px', height: '32px' }}
                        onClick={() => setPagination(prev => ({ ...prev, page: item }))}
                    >
                        {item}
                    </button>
                )}
            </li>
        ));
    };

    return (
        <div id="notif-email-list">
            <div className="container-fluid p-0">
                <div className="row g-0 mh-100vh">
                    <div className="col-md-2 border-end">
                        <div className="d-md-flex d-none align-items-center justify-content-center p-1 h-48">
                            <a className="h-100" href="/dashboard"><img className="h-100" src="/cuancuan.png" alt="PointStar" /></a>
                        </div>
                        <hr className="m-0" />
                        <Menu active="support-notif" />
                    </div>
                    <div className="col-md-10">
                        <Header />
                        <hr className="m-0" />
                        <div className="main">
                            <div className="p-4">
                                <section className="mb-4">
                                    <div className="d-flex align-items-center justify-content-between mb-3">
                                        <div className="fs-24">Notif Email</div>
                                        <div className="d-flex align-items-center justify-content-between gap-2">
                                            <div className="input-group input-search">
                                                <button className="btn bg-white" type="button" onClick={() => clearSearch()}>{tableSearch ? <i className="bi bi-x-lg" /> : <i className="bi bi-search" />}</button>
                                                <input className="form-control" type="text" placeholder="Search" value={tableSearch} onChange={(e) => setTableSearch(e.target.value)} ref={searchInputRef} autoFocus />
                                            </div>
                                            {crmUser.user_level !== 'View' && (
                                                <button className="btn btn-primary bg-gold text-dark border-0" type="button" onClick={handleCreate}> <i className="bi bi-plus-circle me-1" />  Add</button>
                                            )}
                                        </div>
                                    </div>
                                    <div className="bg-white border-radius-4 p-4">
                                        {isLoadingPage ? (
                                            <p>Loading...</p>
                                        ) : (
                                            data && data.length > 0 ? (
                                                <>
                                                    <div className="table-responsive">
                                                        <table className="table table-hover">
                                                            <thead>
                                                                <tr>
                                                                    <th className="bg-gold w-40">#</th>
                                                                    <th className="bg-gold w-300">Name</th>
                                                                    <th className="bg-gold w-300">Email</th>
                                                                    {crmUser.user_level !== 'View' && (
                                                                        <th className="bg-gold w-120 text-center">Actions</th>
                                                                    )}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {data.map((item, index) => (
                                                                    <tr key={item.id}>
                                                                        <td valign="middle" align="center">{((pagination.page - 1) * pagination.limit) + index + 1}</td>
                                                                        <td valign="middle" className="text-center"><strong>{item.userName || '-'}</strong></td>
                                                                        <td valign="middle" className="text-center">{item.userEmail || '-'}</td>
                                                                        {crmUser.user_level !== 'View' && (
                                                                            <td valign="middle" className="text-center">
                                                                                <button className="btn btn-sm btn-link text-primary" onClick={() => handleEdit(item)}><i className="bi bi-pencil" /></button>
                                                                                <button className="btn btn-sm btn-link text-danger" onClick={() => handleDeleteClick(item.id)}><i className="bi bi-trash" /></button>
                                                                            </td>
                                                                        )}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                    {pagination.totalPages > 1 && (
                                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                                            <div className="d-flex align-items-center gap-2 text-secondary">
                                                                <small>Show</small>
                                                                <select className="form-select form-select-sm border-secondary text-secondary" style={{ width: '70px', borderRadius: '4px' }} value={pagination.limit} onChange={(e) => setPagination(prev => ({ ...prev, limit: Number(e.target.value), page: 1 }))}>
                                                                    <option value={10}>10</option>
                                                                    <option value={20}>20</option>
                                                                    <option value={50}>50</option>
                                                                </select>
                                                                <small>entries</small>
                                                            </div>
                                                            <nav>
                                                                <ul className="pagination pagination-sm mb-0 gap-1">
                                                                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                                        <button className="page-link border-0 text-secondary bg-transparent" onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} disabled={pagination.page === 1}>
                                                                            <i className="bi bi-chevron-left" />
                                                                        </button>
                                                                    </li>
                                                                    {renderPaginationItems()}
                                                                    <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                                                        <button className="page-link border-0 text-secondary bg-transparent" onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} disabled={pagination.page === pagination.totalPages}>
                                                                            <i className="bi bi-chevron-right" />
                                                                        </button>
                                                                    </li>
                                                                </ul>
                                                            </nav>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <div className="text-center p-4">No data available</div>
                                            )
                                        )}
                                    </div>
                                </section>
                            </div>
                        </div>
                    </div>

                    <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                        <Modal.Header closeButton>
                            <Modal.Title>Confirm Delete</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <p>Are you sure you want to delete this Notif Email?</p>
                            <div className="d-flex justify-content-end gap-2 mt-4">
                                <button className="btn btn-secondary" type="button" onClick={() => setShowDeleteModal(false)} disabled={isLoadingPage}>Cancel</button>
                                <button className="btn btn-danger" type="button" onClick={confirmDelete} disabled={isLoadingPage}>Delete</button>
                            </div>
                        </Modal.Body>
                    </Modal>

                    <Modal show={showModal} onHide={() => setShowModal(false)}>
                        <Modal.Header closeButton>
                            <Modal.Title>{modalType === 'create' ? 'Create' : 'Edit'} Notif Email</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <div className="form-group mb-3">
                                <label className="form-label">User <span className="text-danger">*</span></label>
                                <Select
                                    options={userOptions}
                                    value={userOptions.find(u => u.value === currentData.userEmail)}
                                    onChange={(opt) => setCurrentData({ ...currentData, userName: opt.full_name, userEmail: opt.email_address })}
                                    placeholder="Select User..."
                                />
                            </div>
                            <div className="d-flex justify-content-end gap-2 mt-4">
                                <button className="btn btn-secondary" type="button" onClick={() => setShowModal(false)} disabled={isLoadingPage}>Cancel</button>
                                <button className="btn btn-primary bg-gold text-dark border-0" type="button" onClick={handleSave} disabled={isLoadingPage}>Save</button>
                            </div>
                        </Modal.Body>
                    </Modal>
                </div>
            </div >
        </div >
    );
};

export default NotifEmail;





